import { useEffect, useState, type ReactElement } from 'react';
import { Close as CloseIcon } from '@mui/icons-material';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';

import type { OnboardingJourney } from '../../hooks/usePlusWebsites';
import { ONBOARDING_JOURNEY_OPTIONS } from './onboardingJourney';

interface OnboardingJourneyModalProps {
  open: boolean;
  initialValue: OnboardingJourney | null;
  isSaving: boolean;
  onClose: () => void;
  onSave: (value: OnboardingJourney) => void;
}

export function OnboardingJourneyModal({
  open,
  initialValue,
  isSaving,
  onClose,
  onSave,
}: OnboardingJourneyModalProps): ReactElement {
  const [value, setValue] = useState<OnboardingJourney | null>(initialValue);

  useEffect(() => {
    if (open) setValue(initialValue);
  }, [open, initialValue]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle className="vtwo:flex vtwo:items-center vtwo:justify-between vtwo:text-sm vtwo:font-bold">
        Patient onboarding journey
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography className="vtwo:text-lg vtwo:font-bold vtwo:text-typography-primary vtwo:mb-3">
          Onboarding Journey type
        </Typography>
        <RadioGroup
          value={value ?? ''}
          onChange={(event) => setValue(event.target.value as OnboardingJourney)}
        >
          {ONBOARDING_JOURNEY_OPTIONS.map((option) => (
            <FormControlLabel
              key={option.value}
              value={option.value}
              control={<Radio sx={{ '&.Mui-checked': { color: '#327D5D' } }} />}
              label={option.label}
            />
          ))}
        </RadioGroup>
      </DialogContent>
      <DialogActions className="vtwo:px-6 vtwo:pb-6 vtwo:gap-3">
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={isSaving}
          className="vtwo:flex-1 vtwo:text-typography-primary vtwo:border-plus-border"
          sx={{ borderRadius: '8px' }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => value && onSave(value)}
          disabled={isSaving || !value}
          className="vtwo:flex-1"
          sx={{
            borderRadius: '8px',
            backgroundColor: '#327D5D',
            '&:hover': { backgroundColor: '#28644A' },
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
